import * as borogove from "borogove";
import { Button, Search, Sidebar } from "@chatscope/chat-ui-kit-react";
import { CircularProgress, Dialog } from "@mui/material";
import { useNavigate, useOutletContext } from "react-router";
import Linkify from "linkify-react";
import { useQueryState } from "nuqs";
import { useState } from "react";
import UnsplashPicker, { InsertIntoApplicationUploader } from "unsplash-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faThumbtack,
	faThumbtackSlash,
	faXmark,
} from "@fortawesome/free-solid-svg-icons";

import { AvatarWithPlaceholder } from "./AvatarWithPlaceholder";
import { AwaitOnce } from "./AwaitOnce";
import { Profile } from "./Profile";

export const ChatDetails = () => {
	const navigate = useNavigate();
	const [participantSearchQ, setParticipantSearchQ] =
		useQueryState("participantQ");
	const [showChooseBackground, setShowChooseBackground] = useState(false);
	const {
		activeChat,
		client,
		setPopupParticipant,
		selectContact,
		setWallpaper,
		persistence,
	} = useOutletContext<{
		activeChat: borogove.Chat;
		client: borogove.Client;
		setPopupParticipant: (s: {
			id: string;
			details: borogove.Participant;
		}) => void;
		selectContact: (f: (contactChat: borogove.Chat) => void) => void;
		setWallpaper: (url: string) => void;
		persistence: borogove.persistence.KeyValueStore & borogove.Persistence;
	}>();

	let activeChatNotifications: "all" | "mute" | "mention" | "reply" = "all";
	if (activeChat) {
		if (activeChat.notificationsFiltered()) {
			if (activeChat.notifyReply()) {
				activeChatNotifications = "reply";
			} else if (activeChat.notifyMention()) {
				activeChatNotifications = "mention";
			} else {
				activeChatNotifications = "mute";
			}
		}
	}

	if (!activeChat) return <div />;

	return (
		<Sidebar position="right" scrollable={false} className={"active"}>
			<Button
				icon={<FontAwesomeIcon icon={faXmark} />}
				onClick={() => navigate(location.pathname.replace(/\/details$/, ""))}
				style={{ float: "right" }}
				title="Close sidebar"
			/>
			<h2>Chat details</h2>
			<code>{activeChat.chatId}</code>

			{(activeChat.status.emoji || activeChat.status.text) && (
				<p className="status" title="Status">
					<span className="emoji">{activeChat.status.emoji}</span>
					<span className="text">{activeChat.status.text}</span>
				</p>
			)}

			{activeChat instanceof borogove.Channel && (
				<p className="description">
					<Linkify options={{ defaultProtocol: "https", truncate: 37 }}>
						{activeChat.description()}
					</Linkify>
				</p>
			)}

			<section>
				<h3>Settings</h3>
				<Button
					icon={
						<FontAwesomeIcon
							icon={
								activeChat.uiState === borogove.UiState.Pinned
									? faThumbtackSlash
									: faThumbtack
							}
						/>
					}
					onClick={() => activeChat.togglePinned()}
				>
					{activeChat.uiState === borogove.UiState.Pinned
						? "Unpin Chat"
						: "Pin Chat"}
				</Button>
				<Button onClick={() => activeChat.close()}>
					{activeChat.getParticipants().length > 2
						? "Leave Chat"
						: "Archive Chat"}
				</Button>
				{activeChat.canInvite() && (
					<Button
						onClick={() =>
							selectContact((contactChat) => activeChat.invite(contactChat))
						}
					>
						Invite new Member
					</Button>
				)}
				<label>
					<span>Notifications</span>
					<select
						onChange={(e) => {
							if (e.target.value === "all") {
								activeChat.setNotifications(false, true, true);
							} else if (e.target.value === "mute") {
								activeChat.setNotifications(true, false, false);
							} else if (e.target.value === "mention") {
								activeChat.setNotifications(true, true, false);
							} else if (e.target.value === "reply") {
								activeChat.setNotifications(true, true, true);
							}
						}}
					>
						<option value="all" selected={activeChatNotifications === "all"}>
							All Messages
						</option>
						<option
							value="mention"
							selected={activeChatNotifications === "mention"}
						>
							Mentions Only
						</option>
						<option
							value="reply"
							selected={activeChatNotifications === "reply"}
						>
							Mentions and Replies Only
						</option>
						<option value="mute" selected={activeChatNotifications === "mute"}>
							Mute
						</option>
					</select>
				</label>

				{activeChat.isBlocked && (
					<Button onClick={() => activeChat.unblock(true)}>Unblock</Button>
				)}
				{!activeChat.isBlocked && (
					<Button onClick={() => activeChat.block()}>Block</Button>
				)}
				{!activeChat.isBlocked && (
					<Button
						onClick={() => activeChat.block(true, activeChat.lastMessage)}
					>
						Report Spam
					</Button>
				)}
				<Button onClick={() => setShowChooseBackground(true)}>
					Set Background
				</Button>

				{activeChat.getParticipants().length > 2 && (
					<>
						<h3>Members</h3>
						{activeChat.getParticipants().length > 15 && (
							<Search
								placeholder="Search by name, ID"
								value={participantSearchQ || ""}
								onChange={(q) => setParticipantSearchQ(q || null)}
								onClearClick={() => setParticipantSearchQ(null)}
							/>
						)}
						<ul className="participants">
							{activeChat
								.getParticipants()
								.sort(
									new Intl.Collator(undefined, {
										numeric: true,
										sensitivity: "base",
									}).compare,
								)
								.map((p) => ({
									id: p,
									details: activeChat.getParticipantDetails(p),
								}))
								.filter(
									({ details }) =>
										!participantSearchQ ||
										details.displayName.includes(participantSearchQ) ||
										details.chat?.chatId?.includes(participantSearchQ),
								)
								.map(({ id, details }) => (
									<li key={`participant-${id}`}>
										<Button
											onClick={() => setPopupParticipant({ id, details })}
										>
											<AvatarWithPlaceholder
												photoUri={details.photoUri}
												placeholderUri={details.placeholderUri}
												size="sm"
											/>
											{details.displayName}
										</Button>
									</li>
								))}
						</ul>
					</>
				)}
				{activeChat.getParticipants().length === 2 && (
					<>
						<h3>Profile</h3>
						{activeChat
							.getParticipants()
							.map((p) => activeChat.getParticipantDetails(p))
							.filter((p) => !p.isSelf)
							.map((p) => (
								<AwaitOnce
									key={activeChat.chatId}
									resolve={() => p.profile(client)}
									fallback={<CircularProgress />}
								>
									{(profile) => <Profile profile={profile} />}
								</AwaitOnce>
							))}
					</>
				)}
			</section>
			<Dialog
				open={showChooseBackground}
				onClose={() => setShowChooseBackground(false)}
				fullWidth={true}
			>
				<div
					style={{
						height:
							window.innerWidth < 600
								? (window.innerWidth * 4) / 3 + "px"
								: "700px",
						maxHeight: "80vh",
						overflowY: "auto",
					}}
				>
					<UnsplashPicker
						applicationName={import.meta.env.VITE_APP_TITLE}
						accessKey={import.meta.env.VITE_UNSPLASH_ACCESS_KEY}
						preferredSize={{ width: screen.width, height: screen.height }}
						Uploader={InsertIntoApplicationUploader}
						defaultSearch="illustration abstract texture"
						onFinishedUploading={(inurl) => {
							const url =
								inurl.replace(/(?:fm|crop|cs|q)=[^&]+&?/g, "") +
								"&auto=format&fit=crop&crop=entropy&q=70";
							persistence.set(`ui#wallpaper/${activeChat?.chatId}`, url);
							setWallpaper(url);
							setShowChooseBackground(false);
						}}
					/>
				</div>
			</Dialog>
		</Sidebar>
	);
};
